import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useRoute } from '@react-navigation/native';

import { Button, Separator, PaymentForm } from '@components';
import { usePaymentMethod } from '@hooks';
import type { IAddPaymentMethodFormProps } from './AddPaymentMethodForm';
import { AnalyticsService, ToastService } from '@services';
import { AuthNavigatorRoutePropType } from '@navigation';
import { parseApiError } from '@utils';
import { useAuthModel } from '@models/AuthModel';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { ConfirmPaymentTypeEnum } from '@constants';

type RoutePropType = AuthNavigatorRoutePropType<'AddPaymentMethod'>;

export const AddPaymentMethodForm = observer(
  ({ onSuccessSignUp, couponStore }: IAddPaymentMethodFormProps) => {
    const { t } = useTranslation();
    const route = useRoute<RoutePropType>();
    const viewer = useViewerModel();
    const auth = useAuthModel();
    const paymentMethod = usePaymentMethod();
    const [isSigningUp, setIsSigningUp] = useState(false);
    const [isPaymentFormComplete, setIsPaymentFormComplete] =
      useState(false);

    const coupon = couponStore.getCouponForTariffPlan(
      route.params.tariffPlan,
    );

    const plan = viewer.tariffPlans.byCode[route.params.tariffPlan];

    async function signUp() {
      try {
        if (!auth.signUpDraft) {
          // This case should never happened
          return;
        }

        setIsSigningUp(true);

        const signUpParams = {
          ...auth.signUpDraft,
          referrer: route.params.invite,
          referrerVideoId: route.params.inviteVideoId,
          tariffPlan: route.params.tariffPlan,
          origin: route.params.origin,
          couponId: coupon?.id,
        };

        await paymentMethod.submit();

        const createPaymentIntentRes =
          await viewer.createSubscriptionPaymentIntent.run({
            tariffPlan: Number(route.params.tariffPlan),
            userInformation: {
              email: signUpParams.email,
              countryCode: signUpParams.countryCode,
              language: signUpParams.language,
            },
            referrer: signUpParams.referrer,
            couponId: signUpParams.couponId,
          });

        if (!createPaymentIntentRes) {
          throw new Error();
        }

        await auth.signUp.run({
          ...signUpParams,
        });

        if (!plan?.price) {
          // This case should never happened
          return;
        }

        const paymentIntent = await paymentMethod.confirmPayment({
          clientSecret: createPaymentIntentRes.clientSecret,
          queryParams: {
            type: ConfirmPaymentTypeEnum.Subscription.toString(),
            tariffPlan: route.params.tariffPlan.toString(),
          },
        });

        if (!paymentIntent) {
          // This case should never happened
          throw new Error();
        }

        const signUpConfirmRes =
          await viewer.confirmSubscriptionPayment.run({
            paymentIntentId: paymentIntent.id,
          });

        if (!signUpConfirmRes) {
          // This case should never happened
          return;
        }

        // In paymentIntent we have amount in cents, (amount 100 is 1$)
        const formattedAmount = paymentIntent.amount / 100;

        AnalyticsService.trackPurchase({
          currency: paymentIntent.currency,
          value: formattedAmount,
          eventId: signUpConfirmRes.conversionEventId,
          code: route.params.tariffPlan,
        });

        setIsSigningUp(false);

        onSuccessSignUp();
      } catch (error) {
        setIsSigningUp(false);
        ToastService.showError(parseApiError(error).message);
      }
    }

    return (
      <View>
        <PaymentForm onChangeStatus={setIsPaymentFormComplete} />
        <Separator />
        <Button
          onPress={signUp}
          isLoading={isSigningUp}
          disabled={!isPaymentFormComplete}
        >
          {t('addPaymentMethod.pay')}
        </Button>
      </View>
    );
  },
);
